/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
    object, string, array, func, shape, bool, arrayOf, number,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import ReactHtmlParser from 'react-html-parser';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getCurrencyData, getDataDictionary, getPassportBundleItemCurrentlyInCart } from '../../../../../state/ducks/App/App-Selectors';
import { getLanguageName } from '../../../../../state/ducks/Member/ducks/Auth/helper/helper';

import ConnectedGraphqlProductSkus from './GraphqlPDPSkus/GraphqlProductSkus';
import ConnectedGraphqlAddToCartContainerUI from './GraphqlAddToCartContainerUI';
import GraphqlSubscriptionContainer from './GraphqlSubscriptionContainer';
import ConnectedGraphqlFoodAddToCart from './GraphqlFoodAddToCart/GraphqlFoodAddToCart';
import PersonalizeYourGift from './PersonalizeYourGift';
import GraphqlAgeVerification from './CommonProductAgeZipVerify/CommonProductAgeZipVerify';
import GraphqlAddonContainer from './GraphqlAddon/GraphqlAddonContainer';
import NBCUMovieBanner from './NBCUMovie/NBCUMovieBanner';
import ProductQuantityUI from './ProductQuantity';
import GCIPersonalization from './GCIPersonalization';
import GoDeluxe from './GraphqlPDPSkus/GoDeluxe';
import DesktopMobile from '../../../../helpers/DesktopMobile/DesktopMobile';
import { clubComponentType, addToCartDataType } from './common/types';
import SnipeBanner from './ExpressShip/SnipeBanner';
import GraphqlPDPPromoContainer from './GraphqlPDPPromotionBlock/GraphqlPDPPromoContainer';
import OneClickPaymentBuilder from './OneClickPayments/OneClickPaymentBuilder';
import SubscriptionProductBannerUI from './SubscriptionProductBannerUI';

const styles = (theme) => ({
    cartWrapper: {
        [theme.breakpoints.down('xs')]: {
            display: 'grid',
        },
    },
    passportBanner: {
        display: 'inline-block',
        width: '60%',
        float: 'left',
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
        [theme.breakpoints.down('xs')]: {
            'grid-row': 2,
            marginTop: 20,
        },
    },
    addToCart: {
        display: 'inline-block',
        float: 'right',
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    addToCartFullWidth: {
        width: '100%!important',
    },
    productName: {
        fontWeight: 400,
        fontSize: 20,
        marginTop: 0,
        marginBottom: 20,
    },
    deliveryMessage: {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        boxSizing: 'border-box',
        color: theme.palette.cms?.clubAvailablility || '#404040',
        textAlign: 'center',
        margin: 0,
        lineHeight: 'normal',
    },
    zipCodeInput: {
        width: '100%',
        display: 'block',  // IE 11
        '& > div': {
            width: '100%', // IE 11
        },
    },
    zipCodeInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            // margin: '25px 0px 0px 50px',
            backgroundColor: theme.palette.colorValid || '#24b200',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-60px 0px 0px 95px',
            fontSize: '18px',
            padding: '5px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipCodeInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"X"',
            fontSize: '17px',
            padding: '5px',
            fontWeight: 'bold',
            margin: '-32px 0px 0px 96px',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    zipInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid || '#24b200',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"ZIP code confirmed eligible "',
            margin: '10px 0px 0px -100px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    zipCodeInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Not eligible for Wine shipment"',
            bottom: '-34px',
            left: 0,
            paddingLeft: '36px',
            width: 'calc(100% - 8px)',
            height: '25px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInput: {
        width: '100%',
        display: 'flex',  // IE 11
        padding: '8px 0px 8px 0px',
        '& > div': {
            width: '100%', // IE 11
        },
    },
    ageInputInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '400',
            content: '"You must be 21 or older to purchase Wine !"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    dateInvalid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            lineHeight: '25px',
            fontSize: '12px',
            border: 'none',
            fontWeight: '400',
            margin: '0px 0px 0px 20px',
            content: '"Please enter a valid date of birth !"',
            height: '22px',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputInvalidIcon: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            margin: '-50px 0px 0px 205px',
            backgroundColor: theme.palette.cms?.errorZipVerifyPDP || '#79000f',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"x"',
            fontSize: '20px',
            padding: '3px',
            fontWeight: 'bold',
            lineHeight: '17px',
            left: '7px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    grid: {
        width: '250px',
        height: '90px',
    },
    ageInputTextValid: {
        position: 'relative',
        '&::before': {
            position: 'absolute',
            zIndex: '999',
            color: theme.palette.colorValid || '#24b200',
            lineHeight: '25px',
            fontSize: '14px',
            fontWeight: '400',
            content: '"Age Confirmed"',
            width: '95px',
            margin: '0px 0px 0px 12px',
            border: 'none',
            transition: 'opacity 0.3s ease-in',
            opacity: 1,
        },
    },
    ageInputValid: {
        '&::after': {
            position: 'absolute',
            zIndex: '999',
            backgroundColor: theme.palette.colorValid || '#24b200',
            color: theme.palette.white,
            textAlign: 'center',
            content: '"✓"',
            margin: '-50px 0px 0px 215px',
            fontSize: '20px',
            padding: '7px',
            fontWeight: 'bold',
            lineHeight: '17px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            animation: 'fadeZoomIn 0.3s ease-in',
            display: 'inline-block', // IE 11
        },
    },
    goDeluxeContainer: {
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    fromValue: {
        fontSize: '16px',
        color: theme.palette.pdp?.priceFromValue || '#585858',
        marginTop: 0,
        [theme.breakpoints.down('sm')]: {
            padding: '0 20px',
        },
    },
});

const GraphqlPDPDataWrapper = ({
    classes,
    product,
    product: {
        availability,
        productSkus,
        brandId,
        partNumber,
        sympathyValue,
        name,
        displayDeliveryCalendar,
    },
    skuConfig,
    deliveryData,
    selectedSku,
    handleSkuChange,
    sortedData,
    presentation_family,
    productUrl,
    subscriptionDetails,
    subscription,
    triggerSubscriptionCheck,
    location,
    brand,
    isPassportBundleItem,
    passportBundleItemInCart,
    skuMonths,
    changeClubMonth,
    changeClubLength,
    clubUpsells,
    selectedClubUpsells,
    changeClubUpsell,
    handlePersonalizeInputChange,
    disableAddToCart,
    getUpdatedPersonalizedProdData,
    personalizedData,
    personalization,
    isAddToCartEnabled,
    updateWineStatus,
    hasWine,
    featureFlags,
    hasAddons,
    selectedAddons,
    handleAddonChange,
    handleMiniCartModalClick,
    quantity,
    setProductQuantity,
    baseProductSku,
    isPassportBundleProduct,
    clubProduct,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
    content,
    currencyData,
    dataDictionary,
}) => {
    if (!product) {
        return null;
    }

    const [movieSelected, selectMovieSku] = useState({});
    const snipeName = product.image?.snipe || '';
    const isSnipe = snipeName !== '';
    const { currencySymbol } = currencyData;
    const language = getLanguageName();

    const showGCISection = () => {
        if (
            selectedSku?.personalization?.personalizationDefDesign
            && selectedSku?.personalization?.personalizationTemplate
            && (featureFlags['is-gci-enabled'] || featureFlags['is-celebration-card-enabled-pdp'])
        ) {
            const { personalizationDefDesign, personalizationTemplate } = selectedSku?.personalization || {};
            if (personalizationDefDesign && personalizationTemplate) {
                return true;
            }
        }
        return false;
    };
    const isMobile = !useMediaQuery('(min-width:600px)');
    const determineLowerPriceProduct = () => {
        const lastValue = sortedData[sortedData.length - 1];
        if (lastValue) {
            return lastValue?.prices[1]?.value;
        }
        return false;
    };

    const productSubscriptionUrl = content?.entries?.[0]?.subscriptions?.product_url;
    const isSubscriptionProductSelected = location.pathname.includes('subscription');
    const isFlowersSubscriptionFeatureEnabled = featureFlags?.['is-18F-subscription-enabled'];

    let isSubscriptionFeatureEnabled = false;
    if (subscription?.isTypeDiscovery) {
        isSubscriptionFeatureEnabled = true; // enabled for discovery type
    } else {
        isSubscriptionFeatureEnabled = featureFlags['is-pdp-subscription-enabled']; // handled from LD
    }

    return (
        <>
            {product?.productType?.toLowerCase() === 'club' && presentation_family === 'food' && (
                <DesktopMobile
                    breakpoint={960}
                    desktop={() => (
                        <>
                            <h1 className={classes.productName}>
                                {ReactHtmlParser(product?.name)}
                            </h1>
                            <p className={classes.deliveryMessage}>{selectedSku?.availability?.deliveryMessage || ''}</p>
                        </>
                    )}
                    mobile={() => (<></>)}
                />

            )}
            {brand.code !== 'PLA' && <p className={classes.fromValue}>{language === '/en' ? 'From' : dataDictionary.From} {currencySymbol}{determineLowerPriceProduct()}</p>}
            <ConnectedGraphqlProductSkus
                skuData={sortedData}
                config={skuConfig}
                handleSkuChange={handleSkuChange}
                selectedSku={selectedSku}
                presentation_family={presentation_family}
                product={product}
                productUrl={productUrl}
                location={location}
                brand={brand}
                skuMonths={skuMonths}
                clubUpsells={clubUpsells}
                changeClubMonth={changeClubMonth}
                changeClubLength={changeClubLength}
                selectedClubUpsells={selectedClubUpsells}
                changeClubUpsell={changeClubUpsell}
                handleMiniCartModalClick={handleMiniCartModalClick}
                baseProductSku={baseProductSku}
                isPassportBundleProduct={isPassportBundleProduct || {}}
                clubProduct={clubProduct}
                isPdpYotpoEnabled={featureFlags['is-pdp-yotpo-enabled']}
            />

            {presentation_family === 'food' && hasAddons
                ? (
                    <div className={classes.addOn}>
                        <GraphqlAddonContainer
                            selectedAddons={selectedAddons}
                            handleAddonChange={handleAddonChange}
                            product={product}
                            skuData={sortedData}
                            brand={brand}
                            handleMiniCartModalClick={handleMiniCartModalClick}
                            brandId={brandId}
                            partNumber={partNumber}
                            categoryId={location.state?.categoryId || ''}
                            subscription={subscription}
                            subscriptionDetails={subscriptionDetails}
                            presentation_family={presentation_family}
                            disableAddToCart={disableAddToCart}
                            isAddToCartEnabled={isAddToCartEnabled}
                            categoryPath={location.state?.categoryPath || ''}
                            categoryName={location.state?.categoryName || ''}
                            selectedSku={selectedSku}
                            productSkus={productSkus}
                            updateWineStatus={updateWineStatus}
                            deliveryData={deliveryData}
                            mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                            handleMobileAgeVerification={handleMobileAgeVerification}
                            addToCartData={addToCartData}
                        />
                    </div>
                )
                : null}
            {showGCISection() ? (
                <GCIPersonalization
                    featureFlags={featureFlags}
                    brand={brand}
                    selectedSku={selectedSku}
                    getUpdatedPersonalizedProdData={getUpdatedPersonalizedProdData}
                />
            ) : null}
            {presentation_family === 'food' && isSnipe
                && (
                    <div>
                        <SnipeBanner snipeName={snipeName} />
                    </div>
                )}
            {hasWine
                ? (
                    <GraphqlAgeVerification
                        classes={classes}
                        updateWineStatus={updateWineStatus}
                        selectedSku={selectedSku}
                        deliveryData={deliveryData}
                        productSkus={productSkus}
                        brand={brand}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        addToCartData={addToCartData}
                        modal={(presentation_family !== 'food')}
                    />
                )
                : null}
            {(presentation_family === 'food' && subscription?.viewSubscription && isSubscriptionFeatureEnabled)
                && <GraphqlSubscriptionContainer subscription={subscription} subscriptionDetails={subscriptionDetails} triggerSubscriptionCheck={triggerSubscriptionCheck} />}
            {(presentation_family === 'food' && product?.isPersonalizable && personalization?.lines?.length > 0 && !showGCISection())
                && <PersonalizeYourGift personalization={personalization} handlePersonalizeInputChange={handlePersonalizeInputChange} getUpdatedPersonalizedProdData={getUpdatedPersonalizedProdData} />}
            {((presentation_family === 'food' && quantity && featureFlags['is-pdp-quantity-input-shown']) ? (
                <ProductQuantityUI quantity={quantity} setProductQuantity={setProductQuantity} />
            ) : null)}
            {presentation_family === 'food' ? <GraphqlPDPPromoContainer parentCategories={product.parentCategories} categoryId={location.state?.categoryId || ''} /> : null}
            {isFlowersSubscriptionFeatureEnabled && productSubscriptionUrl && (
                <SubscriptionProductBannerUI
                    location={location}
                    content={content}
                />
            )}
            <div className={classes.cartWrapper}>
                {featureFlags?.['is-nbcu-movie-flow-enabled'] && location?.state?.showMovieUpsell && <NBCUMovieBanner skuData={location.state.showMovieUpsell} selectMovieSku={selectMovieSku} movieSelected={movieSelected} />}
                <div className={`${presentation_family === 'food' ? classes.addToCart : null} ${(product?.productType?.toLowerCase() === 'club' && !(!isPassportBundleItem && !passportBundleItemInCart && !isSubscriptionProductSelected)) ? classes.addToCartFullWidth : ''} `}>
                    {presentation_family === 'food'
                        && (
                            <div>
                                <ConnectedGraphqlFoodAddToCart
                                    isAddToCartEnabled={isAddToCartEnabled}
                                    selectedItemId={selectedSku.id}
                                    brandId={brandId}
                                    productSkus={productSkus}
                                    partNumber={partNumber}
                                    categoryId={location.state?.categoryId || ''}
                                    categoryPath={location.state?.categoryPath || ''}
                                    categoryName={location.state?.categoryName || ''}
                                    subscription={subscription}
                                    subscriptionDetails={subscriptionDetails}
                                    fullWidth={(product?.productType?.toLowerCase() === 'club')}
                                    disableAddToCart={disableAddToCart}
                                    personalizedData={personalizedData}
                                    isPersonalizable={product?.isPersonalizable}
                                    product={product}
                                    presentation_family={presentation_family}
                                    handleMiniCartModalClick={handleMiniCartModalClick}
                                    quantity={quantity}
                                    handleMobileAgeVerification={handleMobileAgeVerification}
                                    isMobile={isMobile}
                                    hasWine={hasWine}
                                    isPassportBundleProduct={isPassportBundleProduct || {}}
                                    productType={selectedSku?.productType}
                                    addons={selectedAddons}
                                    // the commented line below is for the movie flow. Food brands are not ready for it currently
                                    // movieSelected={movieSelected}
                                />
                                <OneClickPaymentBuilder selectedSku={selectedSku} hasWine={hasWine} />
                            </div>
                        )}
                    {presentation_family !== 'food'
                        && (
                            <ConnectedGraphqlAddToCartContainerUI
                                productName={name}
                                productDeliveryType={availability?.productDeliveryType}
                                productSkus={productSkus}
                                brandId={brandId}
                                config={skuConfig}
                                skuData={sortedData}
                                selectedSku={selectedSku}
                                deliveryData={deliveryData}
                                partNumber={partNumber}
                                sympathyValue={sympathyValue}
                                isPassportBundleItem={isPassportBundleItem}
                                displayDeliveryCalendar={displayDeliveryCalendar ?? true}
                                movieSelected={movieSelected.skuSelected ? movieSelected : null}
                                isPassportBundleProduct={isPassportBundleProduct || {}}
                                categoryId={location.state?.categoryId || ''}
                                categoryPath={location.state?.categoryPath || ''}
                                categoryName={location.state?.categoryName || ''}
                                handleMobileAgeVerification={handleMobileAgeVerification}
                                hasWine={hasWine}
                            />
                        )}
                </div>
            </div>
            {presentation_family === 'food' && sortedData?.length > 1 && !clubProduct
                && (
                    <div className={classes.goDeluxeContainer}>
                        <GoDeluxe
                            selectedSku={selectedSku}
                            handleSkuChange={handleSkuChange}
                            goDeluxeUpsells={product?.upsells}
                        />
                    </div>
                )}
        </>
    );
};

GraphqlPDPDataWrapper.propTypes = {
    classes: object.isRequired,
    skuConfig: object.isRequired,
    product: object.isRequired,
    deliveryData: object.isRequired,
    partNumber: string,
    hasWine: bool.isRequired,
    presentation_family: string.isRequired,
    selectedSku: object.isRequired,
    handleSkuChange: func.isRequired,
    sortedData: array.isRequired,
    productUrl: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    triggerSubscriptionCheck: func,
    location: object.isRequired,
    brand: shape({
        domain: string,
    }),
    isPassportBundleItem: bool,
    passportBundleItemInCart: bool.isRequired,
    skuMonths: object, // dynamic month named keys
    changeClubMonth: func,
    changeClubLength: func,
    clubUpsells: clubComponentType,
    selectedClubUpsells: string,
    changeClubUpsell: func,
    displayDeliveryCalendar: bool,
    handlePersonalizeInputChange: func,
    disableAddToCart: bool,
    getUpdatedPersonalizedProdData: func,
    personalizedData: shape({
        personalizationType: string,
        personalizationLines: arrayOf({
            perAttributeValue: string,
        }),
    }),
    personalization: shape({
        lines: arrayOf(
            shape({
                lineNumber: number,
                maxChars: number,
            }),
        ),
    }),
    isAddToCartEnabled: bool.isRequired,
    updateWineStatus: func,
    hasAddons: bool,
    selectedAddons: arrayOf(string),
    handleAddonChange: func,
    featureFlags: object.isRequired,
    handleMiniCartModalClick: func.isRequired,
    quantity: string,
    setProductQuantity: func,
    baseProductSku: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ),
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    clubProduct: bool,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
    content: shape({
        entries: arrayOf(shape({
            subscriptions: shape({
                url: string.isRequired,
            }).isRequired,
            url: string.isRequired,
        }).isRequired),
    }).isRequired,
    currencyData: shape({
        currencySymbol: string.isRequired,
    }),
    dataDictionary: object.isRequired,
};

GraphqlPDPDataWrapper.defaultProps = {
    partNumber: null,
    subscriptionDetails: {},
    subscription: {},
    clubUpsells: [],
    skuMonths: {},
    brand: {},
    isPassportBundleItem: false,
    changeClubMonth: () => { },
    changeClubLength: () => { },
    triggerSubscriptionCheck: () => {},
    selectedClubUpsells: null,
    changeClubUpsell: () => { },
    handlePersonalizeInputChange: () => {},
    disableAddToCart: false,
    getUpdatedPersonalizedProdData: () => { },
    personalizedData: {},
    personalization: {},
    updateWineStatus: () => {},
    hasAddons: false,
    displayDeliveryCalendar: true,
    selectedAddons: [],
    handleAddonChange: () => {},
    quantity: '',
    setProductQuantity: () => { },
    baseProductSku: null,
    isPassportBundleProduct: {},
    clubProduct: false,
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => {},
    addToCartData: {},
    currencyData: {},
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    passportBundleItemInCart: getPassportBundleItemCurrentlyInCart(state),
    currencyData: getCurrencyData(state),
    dataDictionary: getDataDictionary(state),
});

export default connect(mapStateToProps)(withStyles(styles)(GraphqlPDPDataWrapper));
